import React, { useEffect, useState } from 'react';
import { Card, Typography, CircularProgress, Box, Tooltip, Fade } from '@mui/material';
import { eventRegistrationApi } from '../api';
import AccountBalanceWalletIcon from '@mui/icons-material/AccountBalanceWallet';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';

interface CreditBalanceProps {
  memberId: string;
  businessId: string;
  facilityId: string;
}

const CreditBalance: React.FC<CreditBalanceProps> = ({ memberId, businessId, facilityId }) => {
  const [balance, setBalance] = useState<number>(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showTooltip, setShowTooltip] = useState(false);

  useEffect(() => {
    const fetchCreditInfo = async () => {
      try {
        const data = await eventRegistrationApi.getMemberCredits(memberId, businessId, facilityId);
        setBalance(data.balance);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'An error occurred');
      } finally {
        setLoading(false);
      }
    };

    fetchCreditInfo();
  }, [memberId, businessId, facilityId]);

  if (loading) {
    return (
      <Card 
        sx={{ 
          p: 2,
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          minWidth: 200,
          height: 80,
          background: 'linear-gradient(145deg, #ffffff 0%, #f5f5f5 100%)',
          boxShadow: '0 4px 12px rgba(0, 0, 0, 0.05)',
          borderRadius: 2,
        }}
      >
        <CircularProgress size={24} thickness={4} />
      </Card>
    );
  }

  if (error) {
    return (
      <Card 
        sx={{ 
          p: 2,
          minWidth: 200,
          height: 80,
          background: 'linear-gradient(145deg, #fff5f5 0%, #ffe3e3 100%)',
          boxShadow: '0 4px 12px rgba(0, 0, 0, 0.05)',
          borderRadius: 2,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <Typography color="error" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <InfoOutlinedIcon fontSize="small" />
          {error}
        </Typography>
      </Card>
    );
  }

  return (
    <Fade in={true} timeout={500}>
      <Card 
        sx={{ 
          p: 2.5,
          minWidth: 200,
          background: 'linear-gradient(145deg, #ffffff 0%, #f8f9fa 100%)',
          boxShadow: '0 4px 12px rgba(0, 0, 0, 0.05)',
          borderRadius: 2,
          border: '1px solid',
          borderColor: 'divider',
          transition: 'all 0.3s ease',
          '&:hover': {
            transform: 'translateY(-2px)',
            boxShadow: '0 6px 16px rgba(0, 0, 0, 0.08)',
          },
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Box 
            sx={{ 
              p: 1.5,
              borderRadius: 2,
              backgroundColor: 'primary.main',
              color: 'white',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <AccountBalanceWalletIcon />
          </Box>
          <Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Typography 
                variant="subtitle2" 
                color="text.secondary"
                sx={{ 
                  fontSize: '0.875rem',
                  fontWeight: 500,
                }}
              >
                Credit Balance
              </Typography>
              <Tooltip 
                title="Credits can be used to pay for classes and events. You earn credits when you cancel registrations at least 24 hours before the start time."
                arrow
                placement="top"
              >
                <InfoOutlinedIcon 
                  fontSize="small" 
                  sx={{ 
                    color: 'text.secondary',
                    cursor: 'help',
                    fontSize: '1rem',
                  }}
                />
              </Tooltip>
            </Box>
            <Typography 
              variant="h5" 
              sx={{ 
                fontWeight: 600,
                color: 'primary.main',
                mt: 0.5,
              }}
            >
              ${balance.toFixed(2)}
            </Typography>
          </Box>
        </Box>
      </Card>
    </Fade>
  );
};

export default CreditBalance; 